import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  Ban,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Download,
  GripVertical,
  Link2,
  Loader2,
  Plus,
  RefreshCw,
  Sparkles,
  Upload,
  X,
} from 'lucide-react';
import {
  getForfeoReserveCalendarViewRange,
  type ForfeoReserveCalendarEventContract,
  type ForfeoReserveCalendarScope,
  type ForfeoReserveCalendarView,
} from '../../../supabase/functions/_shared/forfeo-reserve-calendar';
import {
  applyForfeoReserveCalendarCommand,
  createForfeoReserveCalendarCommand,
  exportForfeoReserveIcal,
  fetchForfeoReserveCalendarSnapshot,
  importForfeoReserveIcal,
} from '../../lib/forfeoReserveCalendarClient';
import '../../styles/commerce-reserve-v21.css';
import { syncForfeoReserveAccommodationInventory } from '../../lib/forfeoReserveInventoryClient';
import {
  proposeForfeoReserveCalendarBlocks,
  type ForfeoReserveCalendarAssistantProposal,
} from '../../lib/forfeoReserveCalendarAssistantClient';
import { humanizeForfeoReserveError } from '../../lib/forfeoReserveEdgeError';

interface Props {
  businessId: string;
  preferredAccommodationId?: string;
  onUpdate?: () => void;
}

const VIEW_LABELS: Record<ForfeoReserveCalendarView, string> = {
  day: 'Jour',
  week: '7 jours',
  month: 'Mois',
};

const SCOPE_LABELS: Record<ForfeoReserveCalendarScope, string> = {
  establishment: 'Établissement',
  service: 'Service',
  resource: 'Ressource',
  employee: 'Employé',
};

function dayKey(value: Date): string {
  return [
    value.getFullYear(),
    String(value.getMonth() + 1).padStart(2, '0'),
    String(value.getDate()).padStart(2, '0'),
  ].join('-');
}

function eventTouchesDay(
  event: ForfeoReserveCalendarEventContract,
  day: Date,
): boolean {
  const starts = new Date(day);
  starts.setHours(0, 0, 0, 0);
  const ends = new Date(starts);
  ends.setDate(ends.getDate() + 1);
  return Date.parse(event.startsAt) < ends.getTime()
    && starts.getTime() < Date.parse(event.endsAt);
}

function errorMessage(value: unknown): string {
  const code = value instanceof Error ? value.message : String(value);
  if (code.includes('calendar_conflict')) {
    return 'Ce déplacement créerait une double réservation ou un blocage concurrent.';
  }
  if (code.includes('revision_conflict')) {
    return 'Le calendrier a changé. Actualisez avant de recommencer.';
  }
  if (code.includes('origin_forbidden')) {
    return 'L’origine du portail doit être autorisée avant l’activation de Reserve.';
  }
  const message = humanizeForfeoReserveError(code);
  return message === code
    ? 'L’opération calendrier n’a pas pu être terminée.'
    : message;
}

const ReserveMerchantCalendar: React.FC<Props> = ({ businessId, preferredAccommodationId, onUpdate }) => {
  const [view, setView] = useState<ForfeoReserveCalendarView>('week');
  const [anchor, setAnchor] = useState(() => new Date());
  const [snapshot, setSnapshot] = useState<Awaited<ReturnType<
    typeof fetchForfeoReserveCalendarSnapshot
  >> | null>(null);
  const [calendarId, setCalendarId] = useState('');
  const [targetKey, setTargetKey] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [draggedEvent, setDraggedEvent] =
    useState<ForfeoReserveCalendarEventContract | null>(null);
  const [calendarName, setCalendarName] = useState('Calendrier principal');
  const [calendarScope, setCalendarScope] =
    useState<ForfeoReserveCalendarScope>('establishment');
  const [manualStart, setManualStart] = useState('');
  const [manualEnd, setManualEnd] = useState('');
  const [reasonCode, setReasonCode] = useState('merchant_manual_block');
  const [assistantPrompt, setAssistantPrompt] = useState('');
  const [assistantLoading, setAssistantLoading] = useState(false);
  const [assistantProposal, setAssistantProposal] = useState<ForfeoReserveCalendarAssistantProposal | null>(null);

  const loadSnapshot = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const inventory = await syncForfeoReserveAccommodationInventory({
        businessId,
        accommodationId: preferredAccommodationId,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
      const preferred = preferredAccommodationId
        ? inventory.items.find((item) => item.accommodationId === preferredAccommodationId)
        : undefined;
      const next = await fetchForfeoReserveCalendarSnapshot(
        businessId,
        anchor,
        view,
      );
      setSnapshot(next);
      setCalendarId((current) => (
        preferred?.calendarId
          || (next.calendars.some((calendar) => calendar.id === current)
            ? current
            : next.calendars[0]?.id || '')
      ));
      if (preferred?.catalogItemId) {
        setTargetKey(`catalog_item:${preferred.catalogItemId}`);
      }
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setLoading(false);
    }
  }, [anchor, businessId, preferredAccommodationId, view]);

  useEffect(() => {
    void loadSnapshot();
  }, [loadSnapshot]);

  const selectedCalendar = snapshot?.calendars.find(
    (calendar) => calendar.id === calendarId,
  );
  const bindings = useMemo(
    () => snapshot?.bindings.filter((binding) => binding.calendarId === calendarId) || [],
    [calendarId, snapshot],
  );
  const events = useMemo(
    () => snapshot?.events.filter((event) => event.calendarId === calendarId) || [],
    [calendarId, snapshot],
  );

  const days = useMemo(() => {
    const range = getForfeoReserveCalendarViewRange(anchor, view);
    const list: Date[] = [];
    const cursor = new Date(range.startsAt);
    const end = new Date(range.endsAt);
    while (cursor < end && list.length < 42) {
      list.push(new Date(cursor));
      cursor.setDate(cursor.getDate() + 1);
    }
    return list;
  }, [anchor, view]);

  const targetLabel = useCallback((event: ForfeoReserveCalendarEventContract) => (
    snapshot?.availableTargets.find(
      (target) => target.id === event.targetId && target.targetType === event.targetType,
    )?.label || 'Ressource'
  ), [snapshot]);

  const runCommand = async (
    command: Parameters<typeof createForfeoReserveCalendarCommand>[0],
    success: string,
  ) => {
    setSaving(true);
    setError('');
    setNotice('');
    try {
      await applyForfeoReserveCalendarCommand(
        createForfeoReserveCalendarCommand(command),
      );
      setNotice(success);
      await loadSnapshot();
      onUpdate?.();
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setSaving(false);
    }
  };

  const createCalendar = async () => {
    await runCommand({
      kind: 'create_calendar',
      businessId,
      name: calendarName,
      scope: calendarScope,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      defaultView: view,
    }, 'Le calendrier Reserve a été créé.');
  };

  const bindTarget = async (key: string) => {
    const [targetType, targetId] = key.split(':');
    if (!calendarId || !targetId) return;
    await runCommand({
      kind: 'bind_calendar',
      businessId,
      calendarId,
      targetType: targetType as 'catalog_item' | 'resource',
      targetId,
    }, 'La cible est maintenant rattachée au calendrier.');
  };

  const selectedTarget = snapshot?.availableTargets.find(
    (target) => `${target.targetType}:${target.id}` === targetKey,
  );
  const selectedTargetIsAccommodation = selectedTarget?.kind === 'room';

  const manualWindowError = useMemo(() => {
    if (!targetKey) return 'Choisissez d’abord l’hébergement ou la ressource à bloquer.';
    if (!manualStart) return 'Choisissez une date de début.';
    if (!manualEnd) return 'Choisissez une date de fin.';
    const startValue = selectedTargetIsAccommodation ? `${manualStart}T00:00:00` : manualStart;
    const endValue = selectedTargetIsAccommodation ? `${manualEnd}T00:00:00` : manualEnd;
    const start = Date.parse(startValue);
    const end = Date.parse(endValue);
    if (!Number.isFinite(start) || !Number.isFinite(end)) return 'Les dates sélectionnées sont invalides.';
    if (end <= start) return 'La date de fin doit être après la date de début.';
    return '';
  }, [manualEnd, manualStart, selectedTargetIsAccommodation, targetKey]);

  const handleManualStartChange = (value: string) => {
    setManualStart(value);
    if (!selectedTargetIsAccommodation || !value) return;
    if (!manualEnd || manualEnd <= value) {
      const next = new Date(`${value}T12:00:00`);
      next.setDate(next.getDate() + 1);
      setManualEnd(dayKey(next));
    }
  };

  const manualIsoWindow = () => ({
    startsAt: new Date(selectedTargetIsAccommodation ? `${manualStart}T00:00:00` : manualStart).toISOString(),
    endsAt: new Date(selectedTargetIsAccommodation ? `${manualEnd}T00:00:00` : manualEnd).toISOString(),
  });

  const requestAssistantProposal = async () => {
    if (!assistantPrompt.trim() || !snapshot) return;
    setAssistantLoading(true);
    setAssistantProposal(null);
    setError('');
    try {
      const [, selectedTargetId] = targetKey.split(':');
      const proposal = await proposeForfeoReserveCalendarBlocks({
        businessId,
        prompt: assistantPrompt.trim(),
        selectedTargetId: selectedTargetId || undefined,
        timezone: selectedCalendar?.timezone || snapshot.timezone,
      });
      setAssistantProposal(proposal);
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setAssistantLoading(false);
    }
  };

  const applyAssistantProposal = async () => {
    if (!assistantProposal || !calendarId) return;
    setSaving(true);
    setError('');
    setNotice('');
    try {
      for (const operation of assistantProposal.operations) {
        await applyForfeoReserveCalendarCommand(
          createForfeoReserveCalendarCommand({
            kind: 'create_manual_block',
            businessId,
            calendarId,
            targetType: operation.targetType,
            targetId: operation.targetId,
            startsAt: operation.startsAt,
            endsAt: operation.endsAt,
            reasonCode: operation.reasonCode,
          }),
        );
      }
      setNotice(
        `${assistantProposal.operations.length} période(s) bloquée(s) après votre confirmation.`,
      );
      setAssistantProposal(null);
      setAssistantPrompt('');
      await loadSnapshot();
      onUpdate?.();
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setSaving(false);
    }
  };

  const createManualBlock = async () => {
    const [targetType, targetId] = targetKey.split(':');
    if (!calendarId || !targetId || manualWindowError) {
      setError(manualWindowError || 'Le blocage ne peut pas être appliqué.');
      return;
    }
    const window = manualIsoWindow();
    await runCommand({
      kind: 'create_manual_block',
      businessId,
      calendarId,
      targetType: targetType as 'catalog_item' | 'resource',
      targetId,
      startsAt: window.startsAt,
      endsAt: window.endsAt,
      reasonCode,
    }, 'La période est bloquée dans Reserve.');
    setManualStart('');
    setManualEnd('');
  };

  const cancelEvent = async (event: ForfeoReserveCalendarEventContract) => {
    if (event.kind !== 'manual_block') return;
    await runCommand({
      kind: 'cancel_manual_block',
      businessId,
      calendarId: event.calendarId,
      eventId: event.id,
      expectedRevision: event.revision,
    }, 'Le blocage manuel est annulé.');
  };

  const moveEventToDay = async (
    event: ForfeoReserveCalendarEventContract,
    targetDay: Date,
  ) => {
    if (event.kind !== 'manual_block') return;
    const oldStart = new Date(event.startsAt);
    const oldEnd = new Date(event.endsAt);
    const duration = oldEnd.getTime() - oldStart.getTime();
    const newStart = new Date(targetDay);
    newStart.setHours(
      oldStart.getHours(),
      oldStart.getMinutes(),
      oldStart.getSeconds(),
      0,
    );
    const newEnd = new Date(newStart.getTime() + duration);
    await runCommand({
      kind: 'move_manual_block',
      businessId,
      calendarId: event.calendarId,
      eventId: event.id,
      startsAt: newStart.toISOString(),
      endsAt: newEnd.toISOString(),
      expectedRevision: event.revision,
    }, 'Le blocage a été déplacé après contrôle des conflits.');
  };

  const changePeriod = (delta: number) => {
    const next = new Date(anchor);
    if (view === 'day') next.setDate(next.getDate() + delta);
    if (view === 'week') next.setDate(next.getDate() + delta * 7);
    if (view === 'month') next.setMonth(next.getMonth() + delta);
    setAnchor(next);
  };

  const importIcal = async (file: File | undefined) => {
    const [targetType, targetId] = targetKey.split(':');
    if (!file || !calendarId || !targetId) {
      setError('Choisissez une cible avant l’import iCal.');
      return;
    }
    if (file.size > 512_000) {
      setError('Le fichier iCal dépasse la limite de 512 Ko.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const result = await importForfeoReserveIcal({
        businessId,
        calendarId,
        targetType: targetType as 'catalog_item' | 'resource',
        targetId,
        ical: await file.text(),
      });
      setNotice(
        `${result.imported} événement(s) importé(s), ${result.conflicts} conflit(s).`,
      );
      await loadSnapshot();
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setSaving(false);
    }
  };

  const exportIcal = async () => {
    if (!snapshot || !calendarId) return;
    setSaving(true);
    setError('');
    try {
      const result = await exportForfeoReserveIcal({
        businessId,
        calendarId,
        startsAt: snapshot.startsAt,
        endsAt: snapshot.endsAt,
      });
      const url = URL.createObjectURL(new Blob([result.ical], {
        type: 'text/calendar;charset=utf-8',
      }));
      const link = document.createElement('a');
      link.href = url;
      link.download = result.filename;
      link.click();
      URL.revokeObjectURL(url);
      setNotice('Le fichier iCal a été exporté.');
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setSaving(false);
    }
  };

  if (loading && !snapshot) {
    return (
      <div className="flex min-h-80 items-center justify-center rounded-3xl border border-slate-200 bg-white" data-commerce-panel="reserve-calendar">
        <Loader2 className="animate-spin text-cyan-600" size={28} />
      </div>
    );
  }

  return (
    <section className="reserve-calendar-v21 reserve-calendar-v213 reserve-calendar-v214 w-full min-w-0 max-w-full overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm" data-forfeo-ui-v218="calendar" data-commerce-panel-v219="calendar" data-commerce-v220-panel="calendar">
      <header className="reserve-calendar-v21__header border-b border-slate-200 bg-white p-5 text-slate-950">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
          <div>
            <div className="flex items-center gap-2 text-xs font-black uppercase tracking-[0.2em] text-blue-600">
              <CalendarDays size={17} /> Forfeo Reserve
            </div>
            <h2 className="mt-2 text-2xl font-black">Calendriers marchands</h2>
            <p className="mt-1 max-w-2xl text-sm text-slate-700">
              Disponibilités par service, ressource et employé, avec conflits contrôlés côté serveur.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {(['day', 'week', 'month'] as ForfeoReserveCalendarView[]).map((item) => (
              <button
                key={item}
                type="button"
                aria-pressed={view === item}
                data-forfeo-v218-calendar-view
                onClick={() => setView(item)}
                className={`rounded-xl px-4 py-2 text-xs font-black uppercase tracking-wider transition ${
                  view === item
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'border border-slate-200 bg-white text-slate-600 hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700'
                }`}
              >
                {VIEW_LABELS[item]}
              </button>
            ))}
            <button
              type="button"
              data-forfeo-v218-calendar-refresh
              onClick={() => void loadSnapshot()}
              className="rounded-xl border border-[#0b2a57] bg-[#0b2a57] p-2.5 text-white shadow-sm hover:bg-[#123a73]"
              aria-label="Actualiser"
            >
              <RefreshCw size={17} className={loading ? 'animate-spin' : ''} />
            </button>
          </div>
        </div>
      </header>

      <div className="reserve-calendar-v21__body min-w-0 space-y-5 bg-white p-4 md:p-6">
        {error && (
          <div className="flex items-start gap-3 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm font-bold text-red-700">
            <AlertTriangle size={18} className="mt-0.5 shrink-0" />
            <span>{error}</span>
            <button type="button" onClick={() => setError('')} className="ml-auto"><X size={16} /></button>
          </div>
        )}
        {notice && (
          <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm font-bold text-emerald-700">
            {notice}
          </div>
        )}

        {!snapshot?.calendars.length ? (
          <div className="rounded-3xl border border-dashed border-cyan-300 bg-cyan-50/50 p-6">
            <h3 className="text-lg font-black text-slate-900">Créer le premier calendrier Reserve</h3>
            <div className="mt-4 grid gap-3 md:grid-cols-3">
              <input
                value={calendarName}
                onChange={(event) => setCalendarName(event.target.value)}
                className="rounded-xl border border-slate-200 bg-white px-4 py-3 font-bold outline-none focus:border-cyan-500"
                placeholder="Nom du calendrier"
              />
              <select
                value={calendarScope}
                onChange={(event) => setCalendarScope(event.target.value as ForfeoReserveCalendarScope)}
                className="rounded-xl border border-slate-200 bg-white px-4 py-3 font-bold outline-none focus:border-cyan-500"
              >
                {Object.entries(SCOPE_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => void createCalendar()}
                disabled={saving}
                className="flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-3 font-black text-white shadow-sm hover:bg-blue-500 disabled:opacity-50"
              >
                <Plus size={17} /> Créer
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="grid gap-4 xl:grid-cols-[1fr_auto]">
              <div className="grid gap-3 md:grid-cols-2">
                <label className="text-xs font-black uppercase tracking-wider text-slate-700">
                  Calendrier
                  <select
                    value={calendarId}
                    onChange={(event) => {
                      setCalendarId(event.target.value);
                      setTargetKey('');
                    }}
                    className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold normal-case tracking-normal text-slate-900"
                  >
                    {snapshot.calendars.map((calendar) => (
                      <option key={calendar.id} value={calendar.id}>{calendar.name}</option>
                    ))}
                  </select>
                </label>
                <label className="text-xs font-black uppercase tracking-wider text-slate-700">
                  Hébergement / ressource
                  <select
                    value={targetKey}
                    onChange={(event) => setTargetKey(event.target.value)}
                    className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold normal-case tracking-normal text-slate-900"
                  >
                    <option value="">Tous les hébergements — vue globale</option>
                    {bindings.filter((binding) => binding.catalogItemId || binding.resourceId).map((binding) => (
                      <option
                        key={binding.id}
                        value={binding.catalogItemId
                          ? `catalog_item:${binding.catalogItemId}`
                          : `resource:${binding.resourceId}`}
                      >
                        {binding.label}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <div className="flex flex-wrap items-end gap-2">
                <label data-forfeo-v218-calendar-ical className="cursor-pointer rounded-xl border border-[#0b2a57] bg-[#0b2a57] px-4 py-3 text-xs font-black uppercase tracking-wider text-white hover:bg-[#123a73]">
                  <Upload size={15} className="mr-2 inline" /> Importer iCal
                  <input
                    type="file"
                    accept=".ics,text/calendar"
                    className="hidden"
                    onChange={(event) => void importIcal(event.target.files?.[0])}
                  />
                </label>
                <button
                  type="button"
                  onClick={() => void exportIcal()}
                  disabled={saving || !calendarId}
                  data-forfeo-v218-calendar-ical
                  className="rounded-xl border border-[#0b2a57] bg-[#0b2a57] px-4 py-3 text-xs font-black uppercase tracking-wider text-white hover:bg-[#123a73] disabled:opacity-50"
                >
                  <Download size={15} className="mr-2 inline" /> Exporter iCal
                </button>
              </div>
            </div>

            {snapshot.availableTargets.some((target) => !bindings.some(
              (binding) => binding.catalogItemId === target.id || binding.resourceId === target.id,
            )) && (
              <div className="rounded-2xl border border-indigo-100 bg-indigo-50 p-4">
                <div className="mb-3 flex items-center gap-2 text-sm font-black text-indigo-900">
                  <Link2 size={17} /> Rattacher un service, une ressource ou un employé
                </div>
                <div className="flex flex-wrap gap-2">
                  {snapshot.availableTargets.filter((target) => !bindings.some(
                    (binding) => binding.catalogItemId === target.id || binding.resourceId === target.id,
                  )).map((target) => (
                    <button
                      key={`${target.targetType}:${target.id}`}
                      type="button"
                      onClick={() => void bindTarget(`${target.targetType}:${target.id}`)}
                      disabled={saving}
                      className="rounded-full bg-white px-3 py-2 text-xs font-bold text-indigo-800 shadow-sm hover:bg-indigo-100 disabled:opacity-50"
                    >
                      + {target.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="flex items-center justify-between rounded-2xl bg-slate-50 p-2">
              <button type="button" onClick={() => changePeriod(-1)} className="rounded-xl p-3 hover:bg-white">
                <ChevronLeft size={18} />
              </button>
              <div className="text-center">
                <div className="text-xs font-black uppercase tracking-wider text-slate-700">
                  {selectedCalendar?.timezone || snapshot.timezone}
                </div>
                <div className="font-black capitalize text-slate-900">
                  {anchor.toLocaleDateString('fr-CA', {
                    month: 'long',
                    year: 'numeric',
                    day: view === 'day' ? 'numeric' : undefined,
                  })}
                </div>
              </div>
              <button type="button" onClick={() => changePeriod(1)} className="rounded-xl p-3 hover:bg-white">
                <ChevronRight size={18} />
              </button>
            </div>

            <div className={`reserve-calendar-v214__days-grid grid gap-2 ${view === 'day' ? 'reserve-calendar-v214__grid--day grid-cols-1' : view === 'week' ? 'reserve-calendar-v214__grid--week grid-cols-7 min-w-[700px] md:min-w-0' : 'reserve-calendar-v214__grid--month grid-cols-7 min-w-[700px] md:min-w-0'}`}>
              {days.map((day) => {
                const dayEvents = events.filter((event) => eventTouchesDay(event, day));
                return (
                  <div
                    key={dayKey(day)}
                    onDragOver={(event) => event.preventDefault()}
                    onDrop={() => {
                      if (draggedEvent) void moveEventToDay(draggedEvent, day);
                      setDraggedEvent(null);
                    }}
                    className={`reserve-calendar-v214__day-card min-h-36 rounded-2xl border p-2 transition ${
                      dayKey(day) === dayKey(new Date())
                        ? 'border-cyan-400 bg-cyan-50/60'
                        : 'border-slate-200 bg-white hover:border-slate-300'
                    }`}
                  >
                    <div className="mb-2 border-b border-slate-100 pb-2 text-center">
                      <div className="text-[10px] font-black uppercase tracking-wider text-slate-700">
                        {day.toLocaleDateString('fr-CA', { weekday: 'short' })}
                      </div>
                      <div className="text-lg font-black text-slate-900">{day.getDate()}</div>
                    </div>
                    <div className="space-y-2">
                      {dayEvents.map((event) => (
                        <div
                          key={event.id}
                          draggable={event.kind === 'manual_block'}
                          onDragStart={() => setDraggedEvent(event)}
                          className={`rounded-xl border p-2 text-[11px] font-bold ${
                            event.kind === 'external_busy'
                              ? 'border-amber-200 bg-amber-50 text-amber-800'
                              : 'cursor-grab border-slate-300 bg-slate-100 text-slate-700 active:cursor-grabbing'
                          }`}
                        >
                          <div className="flex items-start gap-1">
                            {event.kind === 'manual_block' ? <GripVertical size={13} /> : <Ban size={13} />}
                            <div className="min-w-0 flex-1">
                              <div className="truncate">{targetLabel(event)}</div>
                              <div className="font-medium opacity-70">
                                {new Date(event.startsAt).toLocaleTimeString('fr-CA', { hour: '2-digit', minute: '2-digit' })}
                                {' – '}
                                {new Date(event.endsAt).toLocaleTimeString('fr-CA', { hour: '2-digit', minute: '2-digit' })}
                              </div>
                            </div>
                            {event.kind === 'manual_block' && (
                              <button type="button" onClick={() => void cancelEvent(event)} aria-label="Annuler le blocage">
                                <X size={13} />
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="reserve-calendar-v213__manual rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h3 className="flex items-center gap-2 font-black text-slate-900">
                    <Ban size={17} /> Rendre une période indisponible
                  </h3>
                  <p className="mt-1 text-sm text-slate-700">
                    {selectedTargetIsAccommodation
                      ? 'Pour un hébergement, choisissez simplement les dates. La date de fin est exclusive.'
                      : 'Choisissez un début et une fin pour la cible sélectionnée.'}
                  </p>
                </div>
                {selectedTarget && (
                  <span className="rounded-full bg-cyan-100 px-3 py-1.5 text-xs font-black text-cyan-900">
                    {selectedTarget.label}
                  </span>
                )}
              </div>

              <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                <label className="text-xs font-black uppercase tracking-wider text-slate-700">
                  Du
                  <input
                    type={selectedTargetIsAccommodation ? 'date' : 'datetime-local'}
                    value={manualStart}
                    onChange={(event) => handleManualStartChange(event.target.value)}
                    className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-3 py-3 text-sm font-bold normal-case tracking-normal text-slate-900"
                  />
                </label>
                <label className="text-xs font-black uppercase tracking-wider text-slate-700">
                  Au
                  <input
                    type={selectedTargetIsAccommodation ? 'date' : 'datetime-local'}
                    value={manualEnd}
                    min={selectedTargetIsAccommodation ? manualStart || undefined : undefined}
                    onChange={(event) => setManualEnd(event.target.value)}
                    className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-3 py-3 text-sm font-bold normal-case tracking-normal text-slate-900"
                  />
                </label>
                <label className="text-xs font-black uppercase tracking-wider text-slate-700">
                  Motif
                  <select
                    value={reasonCode}
                    onChange={(event) => setReasonCode(event.target.value)}
                    className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-3 py-3 text-sm font-bold normal-case tracking-normal text-slate-900"
                  >
                    <option value="merchant_manual_block">Indisponible</option>
                    <option value="maintenance">Entretien</option>
                    <option value="renovation">Rénovation</option>
                    <option value="owner_use">Usage propriétaire</option>
                    <option value="private_event">Événement privé</option>
                  </select>
                </label>
                <button
                  type="button"
                  onClick={() => void createManualBlock()}
                  disabled={saving || Boolean(manualWindowError)}
                  title={manualWindowError || 'Bloquer cette période'}
                  className="mt-5 flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-3 text-sm font-black text-white shadow-sm hover:bg-blue-500 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  {saving ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
                  Bloquer ces dates
                </button>
              </div>
              {manualWindowError && (
                <p className="mt-3 text-sm font-bold text-amber-700">{manualWindowError}</p>
              )}
            </div>

            <div className="rounded-3xl border border-violet-200 bg-violet-50/70 p-5">
              <div className="flex items-center gap-2 font-black text-violet-950">
                <Sparkles size={18} /> Assistant Calendrier Forfeo Reserve
              </div>
              <p className="mt-1 text-sm text-violet-800/80">
                Décrivez simplement ce que vous voulez bloquer. L’Assistant Forfeo Reserve prépare une proposition; rien n’est modifié sans votre confirmation.
              </p>
              <div className="mt-4 flex flex-col gap-3 lg:flex-row">
                <textarea
                  value={assistantPrompt}
                  onChange={(event) => setAssistantPrompt(event.target.value)}
                  rows={3}
                  maxLength={2000}
                  placeholder="Ex. Bloque la chambre lit King du 17 au 20 août pour rénovation."
                  className="min-h-24 flex-1 rounded-2xl border border-violet-200 bg-white px-4 py-3 text-sm font-semibold text-slate-900 outline-none focus:border-violet-500"
                />
                <button
                  type="button"
                  onClick={() => void requestAssistantProposal()}
                  disabled={assistantLoading || !assistantPrompt.trim()}
                  className="inline-flex min-w-40 items-center justify-center gap-2 rounded-2xl bg-violet-700 px-5 py-3 font-black text-white disabled:opacity-40"
                >
                  {assistantLoading ? <Loader2 size={17} className="animate-spin" /> : <Sparkles size={17} />}
                  Analyser
                </button>
              </div>

              {assistantProposal && (
                <div className="mt-4 rounded-2xl border border-violet-200 bg-white p-4">
                  <div className="font-black text-slate-900">{assistantProposal.summary}</div>
                  <div className="mt-3 space-y-2">
                    {assistantProposal.operations.map((operation, index) => (
                      <div key={`${operation.targetId}:${operation.startsAt}:${index}`} className="rounded-xl bg-slate-50 p-3 text-sm text-slate-700">
                        <div className="font-black text-slate-900">{operation.targetLabel}</div>
                        <div>{new Date(operation.startsAt).toLocaleDateString('fr-CA', { day: 'numeric', month: 'long', year: 'numeric' })} → {new Date(operation.endsAt).toLocaleDateString('fr-CA', { day: 'numeric', month: 'long', year: 'numeric' })}</div>
                        {operation.explanation && <div className="mt-1 text-xs text-slate-700">{operation.explanation}</div>}
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => void applyAssistantProposal()}
                      disabled={saving}
                      className="rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-black text-white shadow-sm hover:bg-blue-500 disabled:opacity-50"
                    >
                      Confirmer et appliquer
                    </button>
                    <button
                      type="button"
                      onClick={() => setAssistantProposal(null)}
                      className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-black text-slate-700"
                    >
                      Modifier ma demande
                    </button>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default ReserveMerchantCalendar;
